import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import styles from './RichTextEditor.module.scss';

const preventFocusSteal = (e) => e.preventDefault();

const ToolbarButton = ({ onClick, isActive, disabled, ariaLabel, children }) => (
	<button
		type="button"
		onClick={onClick}
		className={isActive ? styles.active : ''}
		disabled={disabled}
		aria-label={ariaLabel}
		aria-pressed={isActive}
		onMouseDown={preventFocusSteal}
	>
		{children}
	</button>
);

const MenuBar = ({ editor, disabled }) => {
	if (!editor) return null;

	return (
		<div className={styles.toolbar} role="toolbar" aria-label="Text formatting">
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleBold().run()}
				isActive={editor.isActive('bold')}
				disabled={disabled}
				ariaLabel="Bold"
			>
				<strong>B</strong>
			</ToolbarButton>
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleItalic().run()}
				isActive={editor.isActive('italic')}
				disabled={disabled}
				ariaLabel="Italic"
			>
				<em>I</em>
			</ToolbarButton>
			<span className={styles.divider} />
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
				isActive={editor.isActive('heading', { level: 2 })}
				disabled={disabled}
				ariaLabel="Heading"
			>
				H
			</ToolbarButton>
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
				isActive={editor.isActive('heading', { level: 3 })}
				disabled={disabled}
				ariaLabel="Subheading"
			>
				<span style={{ fontSize: '0.85em' }}>H</span>
			</ToolbarButton>
			<span className={styles.divider} />
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleBulletList().run()}
				isActive={editor.isActive('bulletList')}
				disabled={disabled}
				ariaLabel="Bullet list"
			>
				&bull;
			</ToolbarButton>
			<ToolbarButton
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
				isActive={editor.isActive('orderedList')}
				disabled={disabled}
				ariaLabel="Numbered list"
			>
				1.
			</ToolbarButton>
		</div>
	);
};

const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const plainTextToHtml = (text) => {
	if (!text) return '';
	if (/<[a-z][\s\S]*>/i.test(text)) return text;
	return text
		.split('\n')
		.map((line) => `<p>${line ? escapeHtml(line) : '<br>'}</p>`)
		.join('');
};

const RichTextEditor = ({ defaultValue, onBlur, disabled, className }) => {
	const initialHtml = plainTextToHtml(defaultValue || '');
	const savedValueRef = useRef(initialHtml);

	const handleBlur = useCallback(({ editor: editorInstance }) => {
		const html = editorInstance.getHTML();
		const isEmpty = editorInstance.isEmpty;
		const newValue = isEmpty ? '' : html;

		if (newValue !== savedValueRef.current) {
			savedValueRef.current = newValue;
			onBlur?.(newValue);
		}
	}, [onBlur]);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				blockquote: false,
				codeBlock: false,
				code: false,
				horizontalRule: false,
				strike: false,
			}),
		],
		content: initialHtml,
		editable: !disabled,
		onBlur: handleBlur,
	});

	return (
		<div className={`${styles.editor} ${className || ''} ${disabled ? styles.disabled : ''}`}>
			<MenuBar editor={editor} disabled={disabled} />
			<EditorContent editor={editor} className={styles.content} />
		</div>
	);
};

export { RichTextEditor };
